// frontend/src/pages/admin/ManagedLogins.tsx
import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../contexts/ToastContext';
import managedLoginService from '../../services/managedLogin.service';
import yardService from '../../services/yard.service';
import {
  ManagedLogin,
  ManagedLoginCreate,
  ManagedLoginUpdate,
  UserType,
} from '../../types/managedLogin';
import { YardLocation } from '../../types/yard';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

export default function ManagedLogins() {
  const { showToast } = useToast();

  const [logins, setLogins] = useState<ManagedLogin[]>([]);
  const [yards, setYards] = useState<YardLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLogin, setEditingLogin] = useState<ManagedLogin | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(showModal);

  // Form state
  const [formData, setFormData] = useState<ManagedLoginCreate>({
    username: '',
    password: '',
    phone_number: '',
    user_type: 'guard',
    display_name: '',
    mfa_enabled: false,
    is_active: true,
    default_location_id: undefined,
  });
  const [confirmPassword, setConfirmPassword] = useState('');

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [loginsData, yardsData] = await Promise.all([
        managedLoginService.getAll(),
        yardService.getYardLocations(),
      ]);
      setLogins(loginsData);
      setYards(yardsData);
    } catch (error) {
      showToast({ message: 'Error loading data', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateModal = () => {
    setEditingLogin(null);
    setFormData({
      username: '',
      password: '',
      phone_number: '',
      user_type: 'guard',
      display_name: '',
      mfa_enabled: false,
      is_active: true,
      default_location_id: undefined,
    });
    setConfirmPassword('');
    setShowModal(true);
  };

  const openEditModal = (login: ManagedLogin) => {
    setEditingLogin(login);
    setFormData({
      username: login.username,
      password: '',
      phone_number: login.phone_number || '',
      user_type: login.user_type,
      display_name: login.display_name || '',
      mfa_enabled: login.mfa_enabled,
      is_active: login.is_active,
      default_location_id: login.default_location_id || undefined,
    });
    setConfirmPassword('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingLogin && !formData.password) {
      showToast({ message: 'Password is required', type: 'error' });
      return;
    }

    if (!/^\+1\d{10}$/.test(formData.phone_number || '')) {
      showToast({
        message: 'Phone number must be 10 digits (US).',
        type: 'error',
      });
      return;
    }

    if (formData.password) {
      if (formData.password !== confirmPassword) {
        showToast({ message: 'Passwords do not match', type: 'error' });
        return;
      }
      const hasLetter = /[A-Za-z]/.test(formData.password);
      const hasDigit = /[0-9]/.test(formData.password);
      if (formData.password.length < 10 || !hasLetter || !hasDigit) {
        showToast({
          message: 'Password must be at least 10 characters and contain a letter and a digit.',
          type: 'error',
        });
        return;
      }
    }

    try {
      if (editingLogin) {
        // Update
        const updateData: ManagedLoginUpdate = {
          display_name: formData.display_name || undefined,
          user_type: formData.user_type,
          is_active: formData.is_active,
          default_location_id: formData.default_location_id,
          phone_number: formData.phone_number,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await managedLoginService.update(editingLogin.id, updateData);
        showToast({ message: 'Login updated successfully', type: 'success' });
      } else {
        // Create
        await managedLoginService.create(formData);
        showToast({ message: 'Login created successfully', type: 'success' });
      }
      setShowModal(false);
      loadData();
    } catch (error: any) {
      showToast({ message: error.response?.data?.detail || 'Error saving login', type: 'error' });
    }
  };

  const handleUnlock = async (login: ManagedLogin) => {
    try {
      await managedLoginService.unlock(login.id);
      showToast({ message: 'Login unlocked.', type: 'success' });
      loadData();
    } catch (error: any) {
      showToast({
        message: error.response?.data?.detail || 'Error unlocking login',
        type: 'error',
      });
    }
  };

  const handleReactivate = async (login: ManagedLogin) => {
    try {
      await managedLoginService.reactivate(login.id);
      showToast({ message: 'Login reactivated.', type: 'success' });
      loadData();
    } catch (error: any) {
      showToast({
        message: error.response?.data?.detail || 'Error reactivating login',
        type: 'error',
      });
    }
  };

  const handleDelete = async (login: ManagedLogin) => {
    if (!confirm(`Are you sure you want to delete "${login.username}"?`)) {
      return;
    }

    try {
      await managedLoginService.delete(login.id);
      showToast({ message: 'Login deleted successfully', type: 'success' });
      loadData();
    } catch (error) {
      showToast({ message: 'Error deleting login', type: 'error' });
    }
  };

  const handleRunMaintenance = async () => {
    if (!confirm('Run rotation/retention sweep now? This will deactivate idle accounts and delete 90-day inactive accounts.')) {
      return;
    }
    try {
      const counts = await managedLoginService.runMaintenance();
      showToast({
        message: `Maintenance complete. Deactivated: ${counts.deactivated}, Warned: ${counts.warned}, Deleted: ${counts.deleted}.`,
        type: 'success',
      });
      loadData();
    } catch (error: any) {
      showToast({
        message: error.response?.data?.detail || 'Error running maintenance',
        type: 'error',
      });
    }
  };

  const filteredLogins = logins.filter((login) => {
    const term = searchTerm.toLowerCase();
    return (
      login.username.toLowerCase().includes(term) ||
      (login.display_name?.toLowerCase() || '').includes(term)
    );
  });

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getYardName = (id: number | null) => {
    if (!id) return '-';
    const yard = yards.find((y) => y.recno === id);
    return yard?.locname || '-';
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Managed Logins</h1>
        <div className="flex gap-2">
          <button
            onClick={handleRunMaintenance}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-label hover:bg-highlight"
            title="Manually run the daily rotation/retention sweep"
          >
            Run Maintenance
          </button>
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Add Login
          </button>
        </div>
      </div>

      <p className="text-gray-600 mb-4">
        Manage guard and customer login accounts for yard access.
      </p>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by username or display name..."
          className="w-full border rounded px-3 py-2"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : (
        <div className="bg-panel rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">
                  Username
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">
                  Display Name
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">
                  Default Yard
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">
                  Last Login
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">
                  Failed Attempts
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">
                  Lockout
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">
                  Last IP
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredLogins.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-muted">
                    {logins.length === 0 ? 'No logins created yet' : 'No matching logins'}
                  </td>
                </tr>
              ) : (
                filteredLogins.map((login) => {
                  const isLocked =
                    !!login.lockout_until &&
                    new Date(login.lockout_until) > new Date();
                  const showUnlock = isLocked || login.failed_login_count > 0;
                  const failedCount = login.failed_login_count ?? 0;
                  let failedBadgeClass = 'bg-gray-100 text-label dark:bg-gray-700';
                  if (failedCount >= 5) {
                    failedBadgeClass = 'bg-red-100 text-red-700';
                  } else if (failedCount >= 3) {
                    failedBadgeClass = 'bg-yellow-100 text-yellow-700';
                  }
                  return (
                    <tr key={login.id} className="hover:bg-highlight">
                      <td className="px-4 py-3 font-medium">{login.username}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                        {login.display_name || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            login.user_type === 'guard'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                          }`}
                        >
                          {login.user_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                        {getYardName(login.default_location_id)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            login.is_active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {login.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted text-sm">
                        {formatDateTime(login.last_login_at)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${failedBadgeClass}`}
                        >
                          {failedCount}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {isLocked ? (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
                            Locked until {formatDateTime(login.lockout_until)}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted text-xs">
                        {login.last_login_ip || '-'}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => openEditModal(login)}
                          className="text-blue-600 hover:text-blue-800 text-sm mr-3"
                        >
                          Edit
                        </button>
                        {showUnlock && login.is_active && (
                          <button
                            onClick={() => handleUnlock(login)}
                            className="text-amber-600 hover:text-amber-800 text-sm mr-3"
                          >
                            Unlock
                          </button>
                        )}
                        {!login.is_active && (
                          <button
                            onClick={() => handleReactivate(login)}
                            className="text-green-600 hover:text-green-800 text-sm mr-3"
                          >
                            Reactivate
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(login)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={panelRef}
            style={panelStyle}
            className="bg-panel rounded-lg shadow-lg w-full max-w-md mx-4"
          >
            <ModalHeader
              title={editingLogin ? 'Edit Login' : 'Create Login'}
              onClose={() => setShowModal(false)}
              dragHandleProps={dragHandleProps}
            />

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  disabled={!!editingLogin}
                  className="w-full border rounded px-3 py-2 disabled:bg-gray-100"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {editingLogin ? 'New Password (leave blank to keep current)' : 'Password'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="w-full border rounded px-3 py-2"
                  required={!editingLogin}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Must be at least 10 characters and contain a letter and a digit.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  required={!editingLogin}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Phone Number</label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 border border-r-0 rounded-l bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-sm">
                    +1
                  </span>
                  <input
                    type="tel"
                    value={(formData.phone_number || '').replace(/^\+1/, '')}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormData({
                        ...formData,
                        phone_number: digits ? `+1${digits}` : '',
                      });
                    }}
                    placeholder="5551234567"
                    maxLength={10}
                    inputMode="numeric"
                    className="w-full border rounded-r px-3 py-2"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  10-digit US phone number. Used for mandatory MFA SMS codes.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Display Name</label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) =>
                    setFormData({ ...formData, display_name: e.target.value })
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">User Type</label>
                <select
                  value={formData.user_type}
                  onChange={(e) =>
                    setFormData({ ...formData, user_type: e.target.value as UserType })
                  }
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="guard">Guard</option>
                  <option value="customer">Customer</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Default Yard</label>
                <select
                  value={formData.default_location_id || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      default_location_id: e.target.value
                        ? Number(e.target.value)
                        : undefined,
                    })
                  }
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">None</option>
                  {yards.map((yard) => (
                    <option key={yard.recno} value={yard.recno}>
                      {yard.locname}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    className="mr-2"
                  />
                  Active
                </label>

              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border dark:border-gray-600 rounded text-label hover:bg-highlight"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingLogin ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
